import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { X, CheckCircle2, XCircle, Clock, RotateCw } from 'lucide-react';
import { fetchPublicProbes } from '../api.ts';
import type { PublicProbesResponse } from '../types.ts';

interface Props {
  /** ISO from/to do período selecionado */
  from: string;
  to: string;
  /** Label exibido no header (ex: "11/03/2026" ou "11/03 14:00") */
  label: string;
  onClose: () => void;
  /** Função customizada para buscar probes (usa fetchPublicProbes por padrão) */
  fetchProbesFn?: (from: string, to: string) => Promise<PublicProbesResponse>;
}

const fmtTime = new Intl.DateTimeFormat('pt-BR', {
  timeZone: 'America/Sao_Paulo',
  day: '2-digit',
  month: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

export function ProbeDetailDrawer({ from, to, label, onClose, fetchProbesFn }: Props) {
  const fetchFn = fetchProbesFn ?? fetchPublicProbes;
  // Filtro "só falhas" — marcado por padrão (a lista costuma ter centenas de UP).
  const [hideUp, setHideUp] = useState(true);
  const { data, isLoading } = useQuery({
    queryKey: [fetchProbesFn ? 'admin' : 'public', 'probes', from, to],
    queryFn: () => fetchFn(from, to),
  });

  // Fechar com ESC
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  // Impedir scroll do body
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const probes = data?.probes ?? [];
  const downCount = probes.filter((p) => p.resultado !== 'UP').length;
  const visibleProbes = hideUp ? probes.filter((p) => p.resultado !== 'UP') : probes;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3 dark:border-gray-800">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
            Probes — {label}
          </h3>
          <button
            onClick={onClose}
            className="rounded-md p-1.5 text-gray-500 hover:bg-gray-100 dark:text-gray-400 dark:hover:bg-gray-800"
          >
            <X size={16} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {isLoading && (
            <p className="text-center text-sm text-gray-400">Carregando probes...</p>
          )}

          {!isLoading && probes.length === 0 && (
            <p className="text-center text-sm text-gray-400">
              Nenhum probe registrado neste período.
            </p>
          )}

          {!isLoading && probes.length > 0 && (
            <div className="space-y-2">
              {/* Contagem + filtro "só falhas" */}
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {hideUp
                    ? `${downCount} falha${downCount !== 1 ? 's' : ''} de ${probes.length} probe${probes.length !== 1 ? 's' : ''}`
                    : `${probes.length} probe${probes.length !== 1 ? 's' : ''} registrado${probes.length !== 1 ? 's' : ''}`}
                </p>
                <label className="flex cursor-pointer items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
                  <input
                    type="checkbox"
                    checked={hideUp}
                    onChange={(e) => setHideUp(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-gray-300 dark:border-gray-600"
                  />
                  Ocultar UP (só falhas)
                </label>
              </div>

              {hideUp && downCount === 0 && (
                <p className="py-6 text-center text-sm text-emerald-600 dark:text-emerald-400">
                  Nenhuma falha neste período.
                </p>
              )}

              {visibleProbes.map((probe, idx) => {
                const isUp = probe.resultado === 'UP';
                return (
                  <div
                    key={idx}
                    className={`rounded-lg border px-3 py-2.5 text-xs ${
                      isUp
                        ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-900 dark:bg-emerald-950/40'
                        : 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/40'
                    }`}
                  >
                    {/* Linha 1: status + hora */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        {isUp ? (
                          <CheckCircle2 size={14} className="text-emerald-600 dark:text-emerald-400" />
                        ) : (
                          <XCircle size={14} className="text-red-600 dark:text-red-400" />
                        )}
                        <span className={`font-semibold ${
                          isUp
                            ? 'text-emerald-700 dark:text-emerald-300'
                            : 'text-red-700 dark:text-red-300'
                        }`}>
                          {probe.resultado}
                        </span>
                      </div>

                      <span className="text-gray-500 dark:text-gray-400">
                        {fmtTime.format(new Date(probe.checkedAt))}
                      </span>
                    </div>

                    {/* Linha 2: detalhes */}
                    <div className="mt-1.5 flex flex-wrap items-center gap-3 text-gray-600 dark:text-gray-400">
                      {probe.statusCode != null && (
                        <span className="flex items-center gap-1">
                          HTTP <strong>{probe.statusCode}</strong>
                        </span>
                      )}
                      {probe.responseTimeMs != null && (
                        <span className="flex items-center gap-1">
                          <Clock size={12} />
                          {probe.responseTimeMs}ms
                        </span>
                      )}
                      {probe.tentativas > 1 && (
                        <span className="flex items-center gap-1">
                          <RotateCw size={12} />
                          {probe.tentativas} tentativas
                        </span>
                      )}
                    </div>

                    {/* Linha 3: erro */}
                    {probe.errorMessage && (
                      <p className="mt-1.5 break-words text-red-600 dark:text-red-400">
                        {probe.errorMessage}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
